// add link to auth page for both Authorize sections on home page
<template>
    <Hero title="Unified Authorization"
        subtitle="Unified manages integration authorization, token refreshes, scopes, and end-user OAuth flows with fully customizable components and source code — no additional tools needed."
        :highlight-words="['Authorization']" :buttons="[
            {
                link: 'https://app.unified.to',
                title: 'Get started free'
            },
            {
                title: 'See documentation',
                link: 'https://docs.unified.to/concepts/embedded-components',
                icon: 'ic:round-keyboard-arrow-right',
            },
        ]" />

    <div class="w-full flex flex-col items-center gap-4">
        <section class="max-w-screen-2xl w-full py-12">
            <img src="/images/auth_screenshot.svg" alt="Embedded Authorization"
                class="w-full h-auto object-contain max-h-[70vh]" />
        </section>

        <section class="pt-24 max-w-full">
            <Customers title="Trusted by developers and product teams worldwide" />
        </section>


        <SeamlessAuth />

        <section class="px-3 lg:w-full sm:py-12 py-32">
            <UseCases title="Copy, paste, authorize"
                subtitle="Authorization doesn't have to be hard — get integrations to market faster" three_col
                :usecases="[
                    {
                        icon: 'heroicons:rectangle-group',
                        // image_url: '/icons/magnifying_glass.svg',
                        title: 'Pre-built UI Components',
                        body: ' Drop in a React, Angular, VueJS, Svelte, or JavaScript component and get instant, frictionless user authorization. Need customization? Access the source code and customize it to fit your brand.'
                        // category: 'ATS',
                        // button: { link: 'https://unified.to/ats', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        icon: 'heroicons:code-bracket',
                        title: 'Comprehensive Auth flows',
                        body: 'Unified handles all variations of OAuth2 flows, API tokens, and integration-specific authorization methods — so you don\'t have to.'

                    },
                    {
                        icon: 'heroicons:shield-check',
                        // image_url: '/icons/dart.svg',
                        title: 'Enterprise-grade security',
                        body: 'SOC 2 compliant. No data storage or caching. Keep API credentials (client secrets, API tokens) locked down in your own secrets manager (AWS, GCP, Azure, or HashiCorp Vault).',
                        // category: 'CRM',
                        // button: { link: 'https://unified.to/crm', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        icon: 'heroicons:arrow-path',
                        // image_url: '/icons/brain.svg',
                        title: 'Automated Authorization Management',
                        body: 'Set it and forget it. Unified.to automates OAuth2 scopes, token refreshes, and lifecycle management—so you never have to debug expired tokens again.',
                        // category: 'ATS',
                        // button: { link: 'https://unified.to/ats', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        icon: 'heroicons:document',

                        // image_url: '/icons/alert.svg',
                        title: 'OAuth2 Credential Guides',
                        body: 'Step-by-step guides to get your own OAuth 2 credentials fast.',
                        // category: 'E-COMMERCE',
                        // button: { link: 'https://unified.to/kms', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        icon: 'heroicons:cube-transparent',
                        // image_url: '/icons/user.svg',
                        title: `Authorize Across ${INTEGRATIONS.length} Integrations`,
                        body: 'Authorize users instantly across ' + INTEGRATIONS.length + ' pre-built integrations spanning ' + CATS.length + '+ API categories—including CRM, ATS, and Accounting. No more building auth from scratch for every new integration.',
                        // category: 'HR',
                        // button: { link: 'https://unified.to/hris', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },

                ]" />
        </section>

        <IntegrationsHero title="Authorization that scales with you"
            :subtitle="`Supports ${INTEGRATIONS.length} integrations across HR, ATS, CRM, Accounting, and many more.`" />


        <section class="max-w-screen-2xl mx-auto w-full py-12 px-6">
            <div class="mb-12 text-center">
                <h2 class="text-4xl font-bold text-white mb-6">Unified Authorization vs Custom OAuth Builds</h2>
                <p class="text-neutral-50 text-xl max-w-3xl mx-auto">Discover the differences between integration
                    solutions and how Unified.to outpaces alternatives in real-time data access, launch speed,
                    maintenance, and AI usefulness.</p>
            </div>
            <ComparisonTable :columns="AUTH_COLUMNS" :sections="AUTH_COMPARISON" :highlight-column-index="0"
                is-text-left-aligned icon_class="text-xl text-green-500" />
        </section>

        <section class="relative flex flex-col justify-center items-center sm:py-12 lg:py-16">
            <div class="h-auto my-15">
                <h2 class="font-semibold">Detailed OAuth2 credential guides</h2>
            </div>

            <div class="flex flex-col gap-8 sm:grid grid-cols-4 mt-8">
                <a target="_blank" :href="guide.button_link" v-for="guide in AUTH_GUIDES" :key="guide.title"
                    class="flex items-center gap-4 border p-8 rounded-lg hover:bg-gray-800 transition-colors">
                    <img :src="guide.image_url" :alt="guide.title" class="w-10 h-10" />
                    <div class="text-lg text-white">{{ guide.title.replace('OAuth2', '') }}
                        <Icon name="ic:round-keyboard-arrow-right" />
                    </div>
                </a>
            </div>
        </section>

        <Testimonials />

        <Hero title="Integrate. Faster." :highlight-words="['faster.']" :buttons="BUTTONS" />

    </div>
</template>


<script lang="ts">
import type { IColumnWithKey, IComparisonSection } from '~/components/ComparisonTable.vue';
import { CATS, INTEGRATIONS } from '~~/services/integrations';
import { setTitle } from '~~/services/common';

export const AUTH_COLUMNS = [
    {
        name: 'Unified.to Authorization',
        // subtitle: 'Extract, Transform, Load',
        key: 'Unified',
        isHighlighted: true,
        // images: ['/logos/Boomi.png', '/logos/Fivetran.png'],
    },
    {
        name: 'Custom-build Authorization',
        // subtitle: 'Integration Platform as a Service',
        key: 'Custom',
        // images: ['/logos/withampersand_logo.jpeg', '/logos/Cyclr.png'],
    },
    {
        name: 'iPass Authorization',
        // subtitle: 'First Generation',
        key: 'iPass',
        // images: ['/logos/Merge.png', '/logos/Kombo.png', '/logos/apideck-white.png'],
    },
] satisfies IColumnWithKey[];


export const AUTH_COMPARISON: IComparisonSection[] = [
    {
        title: '',
        rows: [
            {
                feature: 'Time to launch',
                text: {
                    Unified: 'Minutes',
                    Custom: 'Weeks',
                    iPass: 'Days',
                },
                // ratings: {
                //     Unified: 1,
                //     Custom: 5,
                //     iPass: 3,
                // }
            },
            {
                feature: 'Customization & White-labeling',
                text: {
                    Unified: 'Yes',
                    Custom: 'Yes',
                    iPass: 'Not'
                },
            },
            {
                feature: 'Pre-built UI',
                text: {
                    Unified: 'Yes',
                    Custom: 'Not',
                    iPass: 'Yes',
                },
            },
            {
                feature: 'Direct API Option',
                text: {
                    Unified: 'Yes',
                    Custom: 'Yes',
                    iPass: 'Not'
                },
            },
            {
                feature: 'Security',
                text: {
                    Unified: 'Yes',
                    Custom: 'Yes',
                    iPass: 'Not',
                },
            },
        ],
    },
] as const;


export const AUTH_GUIDES = [{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/slack.png',
    title: 'Slack OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_a_slack_developer_account_and_get_oauth_2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/workday.png',
    title: 'Workday OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_a_workday_developer_app_and_get_oauth2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/hubspot.svg',
    title: 'HubSpot OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_get_your_hubspot_developer_key_and_oauth_2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/github.png',
    title: 'GitHub OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_obtain_your_github_oauth2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/salesforce.png',
    title: 'Salesforce OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_a_salesforce_developer_app_and_get_oauth_2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/google.png',
    title: 'Google OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_a_google_developer_app_and_get_oauth_2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/atlassian.png',
    title: 'Atlassian OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_an_atlassian_developer_app_and_get_oauth2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/microsoft.svg',
    title: 'Microsoft OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_get_your_microsoft_azure_ad_oauth_2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/pipedrive.png',
    title: 'Pipedrive OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_get_your_oauth_2_credentials_in_pipedrive',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/zohocrm.png',
    title: 'Zoho OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_a_zoho_developer_app_and_get_oauth2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/googlemail.svg',
    title: 'Gmail OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_get_your_oauth_2_credentials_for_gmail',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/greenhouse.png',
    title: 'Greenhouse OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/setting_up_oauth_2_credentials_for_greenhouse_ingestion_api',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/discord.png',
    title: 'Discord OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_get_your_discord_oauth_2_credentials_and_bot_token',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/adp.png',
    title: 'ADP OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_an_adp_developer_app_and_get_oauth2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/highlevel.png',
    title: 'HighLevel OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_a_highlevel_developer_app_and_get_oauth2_credentials',
},
{
    image_40: true,
    image_url: 'https://api.unified.to/docs/images/zendesk.png',
    title: 'Zendesk OAuth2',
    button_text: 'Read guide',
    button_link: 'https://docs.unified.to/guides/how_to_register_a_zendesksell_developer_app_and_get_oauth2_credentials',
}]


export default {
    setup() {
        setTitle(
            'Unified Authorization',
            'Unified manages integration authorization, token refreshes, scopes, and end-user OAuth flows with fully customizable components and source code — no additional tools needed.'
        );
        return {
            CATS,
            INTEGRATIONS,
            AUTH_COLUMNS,
            AUTH_COMPARISON,
            AUTH_GUIDES,
            BUTTONS: [
                { link: 'https://app.unified.to', title: 'Get started free' },
                { link: '/contact', title: 'Speak to an expert', icon: 'ic:round-keyboard-arrow-right' },
            ]
        }
    }
}
</script>